import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import api, { setAccessToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(undefined); // undefined = loading
  const [memberships, setMemberships] = useState([]);
  const [orgAccessLost, setOrgAccessLost] = useState(false);
  const [activeOrgId, setActiveOrgIdState] = useState(
    () => localStorage.getItem("lodge_active_org") || null
  );

  const applyMemberships = useCallback((mships) => {
    const list = mships || [];
    const ids = list.map((m) => m.org.id);
    const prevStored = localStorage.getItem("lodge_active_org");
    setMemberships(list);
    setActiveOrgIdState((prev) => {
      const current = prev || prevStored;
      if (current && ids.includes(current)) return current;
      const next = ids[0] || null;
      if (next) localStorage.setItem("lodge_active_org", next);
      else localStorage.removeItem("lodge_active_org");
      return next;
    });
    // Access-lost signal: a previously-active org is no longer available AND the
    // user now has no usable (non-expired) membership — e.g. an expired Demo
    // membership. Brand-new users (no prior stored org) are NOT treated as lost.
    setOrgAccessLost(Boolean(prevStored) && !ids.includes(prevStored) && list.length === 0);
  }, []);

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data.user);
      applyMemberships(data.memberships);
    } catch {
      // No valid session (expected when logged out) — clear auth state.
      setUser(null);
      setMemberships([]);
    }
  }, [applyMemberships]);

  useEffect(() => {
    loadMe();
  }, [loadMe]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setAccessToken(data.token);
    setUser(data.user);
    applyMemberships(data.memberships);
    return data;
  }, [applyMemberships]);

  const register = useCallback(async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setAccessToken(data.token);
    // Brand-new account: never inherit a stale active-org left in localStorage by a
    // previous session. Without this, applyMemberships would flag orgAccessLost and the
    // guard would wrongly show "Accès de démonstration expiré" instead of /onboarding.
    localStorage.removeItem("lodge_active_org");
    setActiveOrgIdState(null);
    setOrgAccessLost(false);
    setUser(data.user);
    applyMemberships(data.memberships);
    return data;
  }, [applyMemberships]);

  const logout = useCallback(async () => {
    try {
      await api.post("/auth/logout");
    } catch (err) {
      // Non-blocking: cookies/token are cleared client-side regardless.
      console.warn("Logout request failed:", err?.message || err);
    }
    setAccessToken(null);
    localStorage.removeItem("lodge_active_org");
    setUser(null);
    setMemberships([]);
    setActiveOrgIdState(null);
    setOrgAccessLost(false);
  }, []);

  const setActiveOrgId = useCallback((id) => {
    localStorage.setItem("lodge_active_org", id);
    setActiveOrgIdState(id);
  }, []);

  const activeMembership = useMemo(
    () => memberships.find((m) => m.org.id === activeOrgId) || null,
    [memberships, activeOrgId]
  );

  // Referral claim: if the user landed via a referral link (?ref=token stored
  // during registration), attach it to their first admin org once available.
  useEffect(() => {
    const ref = localStorage.getItem("lodge_ref");
    if (!ref || !user) return;
    const adminOrg = memberships.find((m) => ["Owner", "Administrator"].includes(m.role));
    if (!adminOrg) return;
    api.post("/referrals/claim", { token: ref, org_id: adminOrg.org.id })
      .catch(() => {})
      .finally(() => localStorage.removeItem("lodge_ref"));
  }, [user, memberships]);

  const value = useMemo(
    () => ({
      user,
      memberships,
      activeOrgId,
      activeMembership,
      orgAccessLost,
      setActiveOrgId,
      login,
      register,
      logout,
      reload: loadMe,
      setUser,
    }),
    [user, memberships, activeOrgId, activeMembership, orgAccessLost, setActiveOrgId, login, register, logout, loadMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
